import { Component, lazy, Suspense } from "react";
import type { ReactNode } from "react";
import type { PortraitFraming } from "./PortraitCanvas.js";
import type { AvatarSpec } from "./spec.js";
import "./humans.css";

// three.js stays out of the main bundle: the portrait canvas loads only where one is shown.
const PortraitCanvas = lazy(() => import("./PortraitCanvas.js"));

function webgl(): boolean {
  try {
    const c = document.createElement("canvas");
    return Boolean(c.getContext("webgl2") ?? c.getContext("webgl"));
  } catch {
    return false;
  }
}

class Boundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(): void {
    // The initials placeholder is the whole response to a 3D failure.
  }
  override render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/**
 * A 3D portrait of a character (a small canvas; the same models and clips as the world). Falls
 * back to the initials when WebGL is unavailable or the models fail to load.
 */
export function AvatarPortrait({
  spec,
  label,
  framing = "bust",
  animate = false,
  topColor,
  className = "",
}: {
  spec: AvatarSpec;
  /** Accessible name; the initials of it fill the fallback. */
  label: string;
  framing?: PortraitFraming;
  animate?: boolean;
  topColor?: string | undefined;
  className?: string;
}) {
  const fallback = (
    <span className="avatar-portrait-initials" aria-hidden="true">
      {label.trim().slice(0, 1).toUpperCase() || "?"}
    </span>
  );
  return (
    <div
      className={`avatar-portrait is-${framing} ${className}`.trim()}
      role="img"
      aria-label={label}
      data-testid="avatar-portrait"
    >
      {webgl() ? (
        <Boundary fallback={fallback}>
          <Suspense fallback={fallback}>
            <PortraitCanvas spec={spec} framing={framing} animate={animate} topColor={topColor} />
          </Suspense>
        </Boundary>
      ) : (
        fallback
      )}
    </div>
  );
}
